import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import CountryCodeSelect, { countryCodes } from './CountryCodeSelect';
import { useGeo } from '../context/GeoContext';
import { submitLead } from '../services/api';

const ContactModal = ({ isOpen, onClose, title = "Get Started Today", packageTitle = '', packageAmount = 0 }) => {
  const geoData = useGeo();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    countryCode: '+1',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  useEffect(() => {
    if (geoData?.data?.countryCode) {
      const match = countryCodes.find(c => c.label === geoData.data.countryCode);
      if (match) {
        setFormData(prev => ({ ...prev, countryCode: match.code }));
      }
    }
  }, [geoData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/[^0-9]/g, '');
      if (digitsOnly.length <= 10) {
        setFormData(prev => ({ ...prev, phone: digitsOnly }));
      }
      if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'email' && value.trim()) {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value)) setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    }
    if (name === 'phone' && value.trim()) {
      if (value.replace(/[^0-9]/g, '').length !== 10) setErrors(prev => ({ ...prev, phone: 'Please enter exactly 10 digits after the country code' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email)) newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (formData.phone.replace(/[^0-9]/g, '').length !== 10) {
      newErrors.phone = 'Please enter exactly 10 digits after the country code';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }

    setIsSubmitting(true);
    setApiError('');

    try {
      const selected = countryCodes.find(c => c.code === formData.countryCode) || countryCodes[0];
      const result = await submitLead({
        name: formData.fullName,
        email: formData.email,
        countryDialCode: formData.countryCode.replace('+', ''),
        country: selected.label,
        phoneNumber: formData.phone,
        description: formData.description,
        packageTitle,
        packageAmount,
        geoData,
      });

      if (result.success) {
        onClose();
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        setApiError(result.error);
      }
    } catch (err) {
      setApiError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh]">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all" data-testid="modal-close-btn">
          <X className="h-5 w-5 text-slate-700" />
        </button>

        <div className="hidden md:block md:w-2/5 relative">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.pexels.com/photos/7841469/pexels-photo-7841469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/60 to-indigo-900/60"></div>
          </div>
          <div className="relative h-full flex flex-col justify-center items-center p-8 text-white">
            <h3 className="text-3xl font-bold mb-4 text-center">Start Your Journey</h3>
            <p className="text-blue-100 text-center text-lg">Let us help you protect your brand and secure your business future.</p>
          </div>
        </div>

        <div className="md:w-3/5 p-5 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">{title}</h2>
          <p className="text-slate-600 text-sm mb-4">Fill out the form below and our team will contact you shortly.</p>

          {apiError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="modal-api-error">
              <p className="text-sm text-red-700">{apiError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
              <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-black bg-white ${errors.fullName ? 'border-red-500' : 'border-slate-300'}`}
                placeholder="John Doe" data-testid="modal-input-fullname" />
              {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
              <input type="text" id="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-black bg-white ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
                placeholder="john@example.com" data-testid="modal-input-email" />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
              <div className="flex gap-2">
                <CountryCodeSelect value={formData.countryCode} onChange={(code) => handleChange({ target: { name: 'countryCode', value: code } })} className="w-32" />
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} onBlur={handleBlur} maxLength={10}
                  className={`flex-1 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-black bg-white ${errors.phone ? 'border-red-500' : 'border-slate-300'}`}
                  placeholder="1234567890" data-testid="modal-input-phone" />
              </div>
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-1">How can we help you?</label>
              <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows="2"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-black bg-white resize-none"
                placeholder="Tell us about your needs..." data-testid="modal-input-description" />
            </div>

            <button type="submit" disabled={isSubmitting} data-testid="modal-submit-btn"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed">
              {isSubmitting ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Submitting...</span> : 'Submit Request'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
